import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles, type Lang } from "@/mocks/articles";
import { MAIN_SITE_URL } from "@/mocks/categories";
import JsonLd from "@/components/feature/JsonLd";
import { buildArticleSchema } from "@/lib/seo";

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const lang = (i18n.language.split("-")[0] as Lang) || "en";
  const list = articles[lang] ?? articles.en;
  const article = list.find((a) => a.slug === slug) ?? list[0];
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={buildArticleSchema(article)} />
      <Navbar />
      <main className="pt-24">
        <article className="mx-auto w-full max-w-[760px] px-4 pb-20 md:px-6">
          {/* 面包屑（含回主页内链） */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">
              {t("nav.home")}
            </Link>
            <span>/</span>
            <Link to="/#guides" className="transition-colors hover:text-primary-700">
              {t("nav.guides")}
            </Link>
            <span>/</span>
            <span className="text-foreground-700">{article.title}</span>
          </nav>

          <h1 className="mt-6 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex w-5 h-5 items-center justify-center">
                <i className="ri-user-line"></i>
              </span>
              {article.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex w-5 h-5 items-center justify-center">
                <i className="ri-calendar-line"></i>
              </span>
              {article.updated}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex w-5 h-5 items-center justify-center">
                <i className="ri-time-line"></i>
              </span>
              {article.readTime}
            </span>
          </div>

          {/* 简短回答 */}
          <div className="mt-6 rounded-lg border border-accent-200 bg-accent-100/60 p-5">
            <p className="text-sm leading-relaxed text-foreground-800 md:text-base">
              {article.shortAnswer}
            </p>
          </div>

          <div className="mt-8 overflow-hidden rounded-lg">
            <img
              src={article.heroImage}
              alt={article.title}
              title={article.title}
              className="h-56 w-full object-cover object-top md:h-72"
            />
          </div>

          {/* 目录 */}
          <nav className="mt-8 rounded-lg border border-foreground-200/70 bg-background-50 p-5">
            <h2 className="font-heading text-sm font-semibold text-foreground-900">
              {t("guides.title")}
            </h2>
            <ol className="mt-3 space-y-2">
              {article.toc.map((item, idx) => (
                <li key={idx}>
                  <a
                    href={`#section-${idx}`}
                    className="text-sm text-foreground-600 transition-colors hover:text-primary-700"
                  >
                    {idx + 1}. {item}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* 正文 */}
          <div className="mt-10 space-y-10">
            {article.sections.map((section, idx) => (
              <section key={idx} id={`section-${idx}`} className="scroll-mt-24">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pIdx) => (
                  <p key={pIdx} className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </div>

          {/* 对比表 */}
          <section id={`section-${article.sections.length}`} className="mt-10 scroll-mt-24">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {article.tableTitle}
            </h2>
            <div className="mt-5 overflow-x-auto rounded-lg border border-foreground-200/70">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead>
                  <tr className="bg-primary-500 text-background-50">
                    {article.table.headers.map((h, idx) => (
                      <th key={idx} className="px-4 py-3 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {article.table.rows.map((row, rIdx) => (
                    <tr key={rIdx} className={rIdx % 2 ? "bg-background-100/60" : "bg-background-50"}>
                      {row.map((cell, cIdx) => (
                        <td
                          key={cIdx}
                          className={`px-4 py-3 ${
                            cIdx === 0 ? "font-medium text-foreground-900" : "text-foreground-600"
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("faq.title")}
            </h2>
            <div className="mt-5 space-y-3">
              {article.faq.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div key={idx} className="overflow-hidden rounded-lg border border-foreground-200/70 bg-background-50">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                    >
                      <span className="text-sm font-semibold text-foreground-900">{item.q}</span>
                      <span className="flex w-6 h-6 shrink-0 items-center justify-center">
                        <i
                          className={`${
                            isOpen ? "ri-subtract-line" : "ri-add-line"
                          } text-lg text-foreground-500`}
                        ></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="border-t border-foreground-200/70 px-5 py-4">
                        <p className="text-sm leading-relaxed text-foreground-600">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* 内链 + CTA */}
          <div className="mt-10 rounded-lg border border-foreground-200/70 bg-background-100/70 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {t("cta.title")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t("cta.subtitle")}</p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("cta.primary")}
                <i className="ri-external-link-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
              >
                {t("nav.home")}
                <i className="ri-arrow-left-line"></i>
              </Link>
            </div>
          </div>

          {/* 相关文章内链 */}
          <div className="mt-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {t("guides.title")}
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {list
                .filter((a) => a.slug !== article.slug)
                .slice(0, 6)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/guide/${a.slug}`}
                    className="rounded-full border border-foreground-200 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    {a.title}
                  </Link>
                ))}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}